import Socket from '../connection/socket';
import * as ShotsActions from '../stores/actions/shots';
import { getState, socketListenerDispatch } from './socket-listener-main';
import { GreenroomState } from '../stores/types/types';
import type { Shot, ShotData } from '../stores/types/shots';
import type { AppSettings } from '../stores/types/settings';
import { setClearShot } from '../stores/actions/settings';

interface loadShotsTriggerMess {
	message: {deprecated: number}
}
function handleLoadShotsTrigger(mess: loadShotsTriggerMess) {
	// this handler completely refreshes all of our shots
	// (reload + replace currentShot w/ new version)
	// this is sent to tell apps that a shot has been edited,
	// but that there is no current synced shot across
	// all apps (as would be the case in `handleSetCurrentShot` handler below)
	const {
		SessionReducer: {
			token,
			meeting,
		},
		HomeReducer: {
			homeMeeting,
		},
	} = getState() as GreenroomState;

	if (mess.message.deprecated) {
		return;
	}
	if (token && meeting?.meeting) {
		socketListenerDispatch(ShotsActions.loadShots(token, meeting.meeting));
	} else if (token && homeMeeting) {
		socketListenerDispatch(ShotsActions.loadShots(token, homeMeeting.meeting));
	}
}

function handleLoadShotTrigger(data: any) {
	// this handler reloads a specific shot
	const {
		SessionReducer: {
			token,
			meeting,
		},
	} = getState() as GreenroomState;
	const { shot_id: shotId, shot_data: shotData } = data.message;
	if (token && meeting?.meeting) {
		socketListenerDispatch(ShotsActions.updateShotData(shotId, shotData));
	}
}

function handlePlayShot(data: any) {
	const {
		SessionReducer: {
			session,
		},
	} = getState() as GreenroomState;
	const shotStartTimestamp = data.message;
	const { from } = shotStartTimestamp.entityValue;

	if (from !== session) {
		socketListenerDispatch(setClearShot(false));
		socketListenerDispatch(ShotsActions.setPlayingShot(shotStartTimestamp));
	}
}

function handleShotInModalEdited(data: any) {
	// when someone makes an edit to the shot in the shot editing modal,
	// if we are also editing that shot, update our modal
	const {
		ShotsReducer: {
			shotModalShot,
		},
	} = getState() as GreenroomState;

	const { shot } = data.message;

	if (shot.shot_id === 0) return;
	if (shotModalShot.shot_id === 0) return;

	if (shot.shot_id === shotModalShot.shot_id) {
		socketListenerDispatch(ShotsActions.setShotModalShot(shot));
	}
}

function handleRequestShotModalEditingState(socket: Socket, message: any) {
	// when someone opens the shot edit modal, check with everyone to see if they already have that
	// shot open in the modal, if they do, send them an update
	const {
		ShotsReducer: {
			shotModalShot,
		},
		SessionReducer: {
			session,
		},
	} = getState() as GreenroomState;

	const { shot, from } = message.message;

	if (from === session) return;
	if (shot.shot_id === 0) return;
	if (shotModalShot.shot_id === 0) return;

	if (shot.shot_id === shotModalShot.shot_id) {
		socket.sendMessage('update-modal-shot', { from: session, shot: shotModalShot });
	}
}

function handleTempAutoPlay(data: any) {
	const { shotId } = data.message;
	// disable temporarily auto play for the current shot
	socketListenerDispatch(ShotsActions.disableShotAutoplay(shotId));
}

function onShotData(data: { message: { shotId: number, shotData: ShotData, thumbnailUrl: string, edited: string, thumbnailGenerating?: boolean } }) {
	const { shotId, shotData, thumbnailUrl, edited, thumbnailGenerating } = data.message;
	socketListenerDispatch(ShotsActions.updateShotData(shotId, shotData, thumbnailUrl, edited));

	if (thumbnailGenerating === true || thumbnailUrl === 'generating') {
		socketListenerDispatch(ShotsActions.addGeneratingThumbnail(shotId));
	} else if (thumbnailGenerating === false) {
		socketListenerDispatch(ShotsActions.removeGeneratingThumbnail(shotId));
	}
}

function onCanvasStyle(data: { message: { canvasStyle: AppSettings}}) {
	const { canvasStyle } = data.message;
	if (canvasStyle) {
		socketListenerDispatch(ShotsActions.updateCanvasStyle(canvasStyle));
	}
}

function handleShotDeleted(message: any) {
	const { shotId, shotOrder } = message.message;

	// this triggers a useEffect in the useShots hook that reloads shots after
	// checking if we need to select a new shot/handle if deleted shot was in our edit modal
	socketListenerDispatch(ShotsActions.setShotIdToDelete(shotId));
	socketListenerDispatch(ShotsActions.setDeletedShotOrder(shotOrder));
	// for front end/reordering
	socketListenerDispatch(ShotsActions.removeShot(shotId));
}

function handleShotAdded(data: {message: { shot: Shot, from: string}}) {
	const { SessionReducer: { session } } = getState() as GreenroomState;
	const { shot, from } = data.message;
	if (from !== session) {
		socketListenerDispatch(ShotsActions.addShot(shot));
	}
}

export default function Shots(socket: Socket) {
	socket.addListener('trigger-load-shots', handleLoadShotsTrigger);
	socket.addListener('trigger-load-shot', handleLoadShotTrigger);
	
	socket.addListener('update-shot-data', onShotData);
	socket.addListener('update-canvas-style', onCanvasStyle);
	// Only beehive will be emitting this message
	if (!socket.isEnablerView) socket.addListener('play-shot', handlePlayShot);
	socket.addListener('shot-deleted', handleShotDeleted);
	socket.addListener('shot-added', handleShotAdded);

	// modal listeners
	socket.addListener('update-modal-shot', handleShotInModalEdited);
	socket.addListener('request-modal-shot', handleRequestShotModalEditingState.bind(null, socket));
	socket.addListener('temp-auto-play', handleTempAutoPlay);
}
